import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamProjectActionsService } from "@application/useCases/workspace/TeamProjectActionsService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { ProjectRoleSchema } from "@domain/schemas/BI";
import { UserPicker } from "@presentation/components/team/UserPicker";
import type { MyTeam, ProjectDetail, UserSummary } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface CreateProjectDialogProps {
  teams: readonly MyTeam[];
  initialTeamId?: string;
  onClose: () => void;
  onSuccess: (created: ProjectDetail) => void;
}

/**
 * Blocks submission without at least one Manager assignment - the one invariant that matters here.
 *
 * Does not yet pre-fill the creator as Manager (WP5.1 polish): the frontend has no reliable way to
 * read the signed-in user's own backend user id today (the session only carries username/tokens),
 * so the manager field starts blank rather than guessing. The invariant itself (zero managers blocks
 * submission) is enforced regardless.
 */
export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({ teams, initialTeamId, onClose, onSuccess }) => {
  const teamProjectActionsService = useInjection(TeamProjectActionsService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const [teamId, setTeamId] = useState(() => teams.find((team) => team.id === initialTeamId)?.id ?? teams[0]?.id ?? "");
  const selectedTeam = teams.find((team) => team.id === teamId);
  const [name, setName] = useState("");
  const [manager, setManager] = useState<UserSummary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = Boolean(selectedTeam) && name.trim().length > 0 && Boolean(manager) && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || !manager || !selectedTeam) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await teamProjectActionsService.createProjectAsync(selectedTeam.id, {
        name: name.trim(),
        initialAssignments: [{ userId: manager.id, role: ProjectRoleSchema.Manager }],
      });
      notifySuccess("project", "created");
      onSuccess(created);
      onClose();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not create the project.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Create project" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label id="create-project-team-label" htmlFor="create-project-team" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Team
          </label>
          <DropDownList
            id="create-project-team"
            ariaLabelledBy="create-project-team-label"
            style={{ width: "100%" }}
            data={[...teams]}
            textField="name"
            dataItemKey="id"
            value={selectedTeam ?? null}
            disabled={isSubmitting}
            onChange={(event) => setTeamId(event.value?.id ?? "")}
          />
        </div>
        <div>
          <label htmlFor="create-project-name" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Project name
          </label>
          <Input id="create-project-name" style={{ width: "100%" }} value={name} onChange={(event) => setName(String(event.value ?? ""))} />
        </div>
        <div>
          <label htmlFor="create-project-manager" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Manager
          </label>
          <UserPicker id="create-project-manager" value={manager} onChange={setManager} />
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>A project must have at least one Manager.</p>
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Create project
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
